import React, { lazy, Suspense, useState } from 'react';
import { toast } from 'react-toastify';
import { url } from '../../../api';
import { apiClient } from '../../../api/client';
import { useAppSelector } from '../../../store/hooks';
import { useConfirm } from '../../../ui/components/ConfirmDialog';
import { coffeeRatingPaths } from '../api/paths';
import './CoffeeCardWeb.css';

import { CardHeader } from './parts/CardHeader';
import { CardImage } from './parts/CardImage';
import { CardActionBar } from './parts/CardActionBar';
import { StatsOverlay } from './parts/StatsOverlay';
import { MapOverlay } from './parts/MapOverlay';
import { CardFooter } from './parts/CardFooter';
import { AdminControls } from './parts/AdminControls';

import { useCardImage } from './hooks/useCardImage';
import { useCardView } from './hooks/useCardView';
import { useCoffeeIcon } from './hooks/useCoffeeIcon';

import type { CoffeeRateInterface } from './types';

// Edit sheet is admin-only and heavy (Leaflet, place search) — lazy load.
const CoffeeCardEditSheet = lazy(() => import('./edit/CoffeeCardEditSheet'));

interface CoffeeCardWebProps {
    coffeeData: CoffeeRateInterface;
    onDelete?: () => void;
    showDeleteButtons?: boolean;
    showUserOverlay?: boolean;
    expandedCardId?: string | null;
    onExpandChange?: (id: string | null) => void;
}

const CoffeeCardWeb: React.FC<CoffeeCardWebProps> = ({
    coffeeData,
    onDelete,
    showDeleteButtons = false,
    showUserOverlay = true,
    expandedCardId = null,
    onExpandChange,
}) => {
    const confirm = useConfirm();
    const user = useAppSelector((state) => state.auth);
    const [editOpen, setEditOpen] = useState(false);

    const coffeeIconUrl = useCoffeeIcon(coffeeData.coffeeType);
    const cardImage = useCardImage(coffeeData._id);
    const cardView = useCardView({
        cardId: coffeeData._id,
        expandedCardId,
        onExpandChange,
    });

    const hasShopCoords = !!(coffeeData.coffeeShopRef?.coordinates?.lat &&
        coffeeData.coffeeShopRef?.coordinates?.lng);
    const canShowMap = hasShopCoords && !coffeeData.isSelfMade;

    const mapCoordinates = (hasShopCoords && coffeeData.coffeeShopRef)
        ? coffeeData.coffeeShopRef.coordinates
        : coffeeData.location.storeCoordinates;

    const isAdmin = user.role === 'admin';

    const handleDelete = async () => {
        const ok = await confirm({
            title: 'Delete coffee entry?',
            message: 'This action cannot be undone.',
            confirmLabel: 'Delete',
            tone: 'danger',
        });
        if (!ok) return;
        try {
            const response = await apiClient.delete(`${url}${coffeeRatingPaths.byId(coffeeData._id)}`, {});
            if (response.status === 200) {
                toast.success('Coffee entry deleted successfully');
                onDelete?.();
            }
        } catch (error) {
            console.error('Error deleting coffee:', error);
            toast.error('Failed to delete coffee entry');
        }
    };

    return (
        <div className='coffee-card-web' ref={cardImage.cardRef}>
            {showDeleteButtons && isAdmin && (
                <AdminControls
                    onEdit={() => setEditOpen(true)}
                    onDelete={handleDelete}
                />
            )}

            <div className='card-content'>
                <CardHeader
                    coffeeData={coffeeData}
                    coffeeIconUrl={coffeeIconUrl}
                    isAdmin={showDeleteButtons && isAdmin}
                />

                <div className='image-section'>
                    <CardImage
                        coffeeData={coffeeData}
                        coffeeIconUrl={coffeeIconUrl}
                        imageUrl={cardImage.imageUrl}
                        imageLoaded={cardImage.imageLoaded}
                        fetchFailed={cardImage.fetchFailed}
                        isVisible={cardImage.isVisible}
                        onImageLoad={cardImage.onImageLoad}
                        onImageError={cardImage.onImageError}
                        showUserOverlay={showUserOverlay}
                        overlayClear={cardView.view === 'image'}
                    />
                    {cardView.view === 'stats' && (
                        <StatsOverlay coffeeData={coffeeData} />
                    )}
                    {cardView.view === 'map' && canShowMap && (
                        <MapOverlay
                            coffeeData={coffeeData}
                            coordinates={mapCoordinates}
                        />
                    )}
                    <CardActionBar
                        onToggleStats={cardView.toggleStats}
                        onToggleMap={cardView.toggleMap}
                        statsActive={cardView.view === 'stats'}
                        mapActive={cardView.view === 'map'}
                        canShowMap={canShowMap}
                        overallRating={coffeeData.overallRating}
                        mapCoordinates={mapCoordinates}
                    />
                </div>

                <CardFooter coffeeData={coffeeData} />
            </div>

            {showDeleteButtons && isAdmin && (
                <Suspense fallback={null}>
                    <CoffeeCardEditSheet
                        open={editOpen}
                        coffeeData={coffeeData}
                        onClose={() => setEditOpen(false)}
                    />
                </Suspense>
            )}
        </div>
    );
};

export default CoffeeCardWeb;
